import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculer la Différence Entre Deux Dates Hégiriens";
const DESCRIPTION =
  "Calculer la Différence Entre Deux Dates Hégiriens pour calculer des dates, durees ou ages avec precision. Outil pratique pour vos demarches administratives, votre organisation personnelle et vos besoins du quotidien.";
const KEYWORDS =
  "r la différence entre deux dates hégiriens, calculatrice r la différence entre deux dates hégiriens, calcul en ligne, age, date, temps, calendrier, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/datehijridifferences/";

// أسماء الأشهر الهجرية
const HIJRI_MONTHS = [
  "Muharram", "Safar", "Rabi' al-awwal", "Rabi' al-Thani", "Jumada al-awwal", "Jumada al-Thani",
  "Rajab", "Sha'ban", "Ramadan", "Shawwal", "Dhu al-Qi'dah", "Dhu al-Hijjah"
];

function DateDifferencesCalculatorHijri() {
  const [d1, setD1] = useState({ day: "1", month: "1", year: "1445" });
  const [d2, setD2] = useState({ day: "1", month: "1", year: "1446" });
  const [differences, setDifferences] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateDifferences = () => {
    setIsLoading(true);
    setError(null);

    // تحويل المدخلات لأرقام
    let day1 = parseInt(d1.day), month1 = parseInt(d1.month), year1 = parseInt(d1.year);
    let day2 = parseInt(d2.day), month2 = parseInt(d2.month), year2 = parseInt(d2.year);

    // ترتيب التواريخ (الأصغر أولاً)
    let start = { y: year1, m: month1, d: day1 };
    let end = { y: year2, m: month2, d: day2 };

    if (year1 > year2 || (year1 === year2 && month1 > month2) || (year1 === year2 && month1 === month2 && day1 > day2)) {
      start = { y: year2, m: month2, d: day2 };
      end = { y: year1, m: month1, d: day1 };
    }

    // حساب الفرق الهجري التقريبي
    let diffYears = end.y - start.y;
    let diffMonths = end.m - start.m;
    let diffDays = end.d - start.d;

    if (diffDays < 0) {
      diffMonths--;
      diffDays += 30; // الشهر الهجري غالباً 29 أو 30
    }
    if (diffMonths < 0) {
      diffYears--;
      diffMonths += 12;
    }

    // حساب الإحصائيات (تقريبية للتقويم الهجري: السنة الهجرية ≈ 354.36 يوم)
    const totalDays = Math.floor(diffYears * 354.36 + diffMonths * 29.53 + diffDays);
    const totalHours = totalDays * 24;
    const totalMinutes = totalHours * 60;
    const totalSeconds = totalMinutes * 60;

    setDifferences({
      years: diffYears,
      months: diffMonths,
      days: diffDays,
      totalDays,
      totalWeeks: Math.floor(totalDays / 7),
      totalMonths: diffYears * 12 + diffMonths,
      totalHours,
      totalMinutes,
      totalSeconds,
      startDate: `${start.d} ${HIJRI_MONTHS[start.m - 1]} ${start.y}`,
      endDate: `${end.d} ${HIJRI_MONTHS[end.m - 1]} ${end.y}`,
      workDays: Math.floor((totalDays / 7) * 5)
    });

    setIsLoading(false);
  };

  const renderSelectGroup = (data, setData, label) => (
    <div className="mb-4">
      <label ><strong>{label}:</strong></label>
      <div className="row">
        <div className="col-md-4">
          <select className="form-control" value={data.day} onChange={(e) => setData({ ...data, day: e.target.value })}>
            {Array.from({ length: 30 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={data.month} onChange={(e) => setData({ ...data, month: e.target.value })}>
            {HIJRI_MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={data.year} onChange={(e) => setData({ ...data, year: e.target.value })}>
            {Array.from({ length: 100 }, (_, i) => 1500 - i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} />

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
          <br />
          <div className="bg-success text-white text-center p-3 rounded shadow-sm">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <p className="mb-4">
              Cette calculatrice est conçue pour calculer la différence entre deux dates en calendrier hégirien. Entrez les deux dates ci-dessous pour obtenir le résultat en années, mois, jours, heures et minutes.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4 p-3 bg-light rounded border">
              <div className="p-2 mb-4 bg-success text-white text-center rounded">
                <strong>Entrez vos dates hégiriennes</strong>
              </div>

              {renderSelectGroup(d1, setD1, "Première date hégirien")}
              {renderSelectGroup(d2, setD2, "Deuxième date hégirien")}

              <div className="text-center mt-4">
                <button className="btn btn-success btn-lg px-5" onClick={calculateDifferences} disabled={isLoading}>
                  {isLoading ? "Calcul en cours..." : "Calculer la Différence"}
                </button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {differences && (
                <div className="mt-5">
                  <div className="alert alert-success border-success">
                    <h3 style={{ fontSize: "18px" }}><strong>Résultat de la Différence Entre les Deux Dates Hégiriennes:</strong></h3>
                    <div className="p-3 bg-white rounded border mt-3 text-center">
                      <strong className="text-muted">Différence:</strong>
                      <div style={{ fontSize: "24px", color: "#28a745", fontWeight: "bold" }}>
                        {differences.years} an(s) {differences.months} mois et {differences.days} jour(s)
                      </div>
                    </div>
                    <div className="mt-4">
                      <p><strong>Détails Complets:</strong></p>
                      <ul>
                        <li><strong>Date de début:</strong> {differences.startDate}</li>
                        <li><strong>Date de fin:</strong> {differences.endDate}</li>
                        <li><strong>Jours totaux:</strong> {differences.totalDays}</li>
                        <li><strong>Semaines totales:</strong> {differences.totalWeeks}</li>
                        <li><strong>Mois totaux:</strong> {differences.totalMonths}</li>
                        <li><strong>Heures totales:</strong> {differences.totalHours}</li>
                        <li><strong>Minutes totales:</strong> {differences.totalMinutes}</li>
                        <li><strong>Secondes totales:</strong> {differences.totalSeconds}</li>
                        <li><strong>Jours de travail:</strong> {differences.workDays}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Différence entre deux dates hégiriennes</h3></div>
              <div className="info-section-body">
                <p>Cette calculatrice mesure le nombre de jours, semaines, mois et années séparant deux dates dans le calendrier islamique. Elle est particulièrement utile pour calculer des intervalles entre fêtes religieuses, des durées de jeune, ou pour les documents notariés utilisant le calendrier hégirien dans les pays arabes.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Précision du calendrier lunaire</h3></div>
              <div className="info-section-body">
                <p>Le début de chaque mois hégirien dépend de l’observation de la nouvelle lune. En pratique, certains pays utilisent des calculs astronomiques préétablis tandis que d’autres attendent l’observation visuelle. Cela peut entraîner une différence d’un jour selon les pays. Les calculs de cette calculatrice sont basés sur les tables astronomiques standard.</p>
              </div>
            </div>            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Comment calcule-t-on la différence entre deux dates hégiriennes ?</strong></h3>
                <p>La calculatrice convertit les deux dates hégiriennes en jours juliens, calcule la différence, puis convertit ce résultat en années, mois et jours hégiriens.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>La calculatrice est-elle précise pour les dates anciennes ?</strong></h3>
                <p>Oui, pour les dates postérieures à l'an 1 de l'hégire (622 apr. J.-C.). Pour les dates antérieures, les systèmes de référence astronomique sont extrapolés.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Puis-je utiliser cette calculatrice pour des événements religieux ?</strong></h3>
                <p>Oui, elle est particulièrement utile pour calculer les intervalles entre Ramadan, Aid Al-Fitr, Aid Al-Adha, Achoura et d'autres événements du calendrier islamique.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Pourquoi les résultats peuvent-ils varier d'un jour selon les pays ?</strong></h3>
                <p>Parce que certains pays déterminent le début du mois hégirien par observation visuelle de la lune, tandis que d'autres utilisent des calculs astronomiques. Cette calculatrice utilise les tables astronomiques standard.</p>
              </div>
            </div>          </div>
        </div>
      </div>
    </div>
  );
}

export default DateDifferencesCalculatorHijri;
